import { Injectable, Injector, inject } from '@angular/core';
import { Router } from '@angular/router';
import { HttpClient, HttpParams } from '@angular/common/http';
import { map, Observable } from 'rxjs';
import { authStore } from './auth.store';
import { AppConfigService } from '@/core/config/app-config.service';
import {
    ApiResponse,
    CreatePasswordDto,
    ForgotPasswordDto,
    LoginRequestDto,
    LoginWebResponse,
    RefreshTokenData,
    RefreshTokenRequestDto,
    ResetPasswordDto,
    SuccessData,
} from './auth.types';
import { normalizeLoginResponse } from './login-response.mapper';

export function decodeJwtPayload(token: string): Record<string, unknown> {
    const base64Url = token.split('.')[1];
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    // atob() decodes to Latin-1 bytes; re-encode as UTF-8 so multi-byte
    // characters in claims (e.g. Arabic names) don't corrupt the JSON.
    const jsonPayload = decodeURIComponent(
        atob(base64)
            .split('')
            .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
            .join('')
    );
    return JSON.parse(jsonPayload);
}

/** True when the JWT has an `exp` claim in the past (or can't be decoded). */
export function isTokenExpired(token: string): boolean {
    try {
        const exp = decodeJwtPayload(token)['exp'];
        return typeof exp === 'number' && exp * 1000 <= Date.now();
    } catch {
        return true;
    }
}

/** HTTP-only auth API. State lives in AuthRepository — components should use that. */
@Injectable({ providedIn: 'root' })
export class AuthService {
    private readonly appConfig = inject(AppConfigService);
    private router = inject(Router);
    private http = inject(HttpClient);
    private injector = inject(Injector);
    private authEndpoint = `${this.appConfig.baseUrl('ffm')}/api/v1/auth`;
    private sessionExpiredShown = false;

    /**
     * Calls LoginWeb. The token comes from `AccessToken` in the body, falling back to the
     * `access-token` response header (only readable cross-origin if the API exposes it).
     */
    login(credentials: LoginRequestDto): Observable<LoginWebResponse> {
        this.sessionExpiredShown = false;
        return this.http
            .post<unknown>(`${this.appConfig.baseUrl('ffm')}/api/User/LoginWeb`, credentials, {observe: 'response'})
            .pipe(
                map((response) => {
                    const body = normalizeLoginResponse(response.body);
                    return {...body, AccessToken: body.AccessToken || response.headers.get('access-token') || ''};
                })
            );
    }

    forgotPassword(email: string) {
        const payload: ForgotPasswordDto = { emailId: email };
        return this.http.post<ApiResponse<SuccessData>>(`${this.authEndpoint}/forgotpassword`, payload);
    }

    sendResetLink(emailId: string) {
        return this.http.post<ApiResponse<SuccessData>>(`${this.authEndpoint}/send-reset-link`, { emailId });
    }

    createPassword(data: CreatePasswordDto) {
        return this.http.post<ApiResponse<SuccessData>>(`${this.authEndpoint}/createpassword`, data);
    }

    resetPassword(data: ResetPasswordDto) {
        return this.http.post<ApiResponse<SuccessData>>(`${this.authEndpoint}/resetpassword`, data);
    }

    /**
     * Changes the logged-in user's password; the user comes from the bearer token.
     * The API takes the passwords as query parameters and replies with plain text.
     */
    changeOwnPassword(oldPassword: string, newPassword: string): Observable<string> {
        const params = new HttpParams().set('oldPassword', oldPassword).set('newPassword', newPassword);
        return this.http.get(`${this.appConfig.baseUrl('ffm')}/api/User/WebLoginPasswordChange`, {
            params,
            responseType: 'text',
        });
    }

    refreshToken(refreshToken: string) {
        const payload: RefreshTokenRequestDto = {
            accessToken: authStore.query((state) => state.token) ?? '',
            refreshToken,
        };
        return this.http.post<ApiResponse<RefreshTokenData>>(`${this.authEndpoint}/refreshtoken`, payload);
    }

    logout() {
        // Suppress further Session Expired dialogs from in-flight requests; reset on next login.
        this.sessionExpiredShown = true;
        authStore.reset();
        this.router.navigateByUrl('/auth/login');
    }

    markSessionExpired(): void {
        this.sessionExpiredShown = true;
    }

    showSessionExpiredDialog() {
        if (this.sessionExpiredShown) return;
        this.sessionExpiredShown = true;

        // Loaded on demand: the alert dialog (and the CDK overlay behind it) stays out of the initial bundle.
        void import('@/shared/zard-ui/components/alert-dialog/alert-dialog.service').then(({ZardAlertDialogService}) =>
            this.injector.get(ZardAlertDialogService).warning({
                zTitle: 'Session Expired',
                zDescription:
                    'For security reasons, your session has expired.\nPlease log out and sign in again to continue using the application.',
                zOkText: 'Logout',
                zClosable: false,
                zMaskClosable: false,
                zOnOk: () => {
                    this.logout();
                },
            }),
        );
    }
}
